import GroupService from "@/services/api/GroupService";
import UserService from "@/services/api/UserService";
import useGlobalStore from "@/services/store/useGlobalStore";
import useUsers from "./useUsers";

const useGroups = () => {
  const { groups, setGroups } = useGlobalStore((state) => state);

  const groupsById: Dic<GroupType> = groups.reduce(
    (curr: Dic<GroupType>, next: GroupType) => ({ ...curr, [next.id]: { ...next } }),
    {}
  );

  const { getUsers, usersById } = useUsers();

  const getGroups = () => {
    return new Promise((resolve) => {
      GroupService.getGroups().then(setGroups).then(resolve);
    });
  };

  const updateGroupsAndUsers = () => {
    return new Promise((resolve) => {
      Promise.all([getGroups(), getUsers()]).then(resolve);
    });
  };

  const createGroup = (colorPalette: GroupColorPalette) => {
    return new Promise((resolve) => {
      GroupService.createGroup(colorPalette).then(updateGroupsAndUsers).then(resolve);
    });
  };

  const deleteGroup = (groupId: number) => {
    return new Promise((resolve) => {
      GroupService.deleteGroup(groupId)
        .then(() => {
          setGroups(groups.filter((group) => group.id !== groupId));
        })
        .then(() => getUsers())
        .then(resolve);
    });
  };

  const updateGroupName = (groupName: string, groupId: string | number) => {
    const group = groupsById[groupId];
    return new Promise((resolve) => {
      GroupService.updateGroup({ ...group, name: groupName })
        .then(getGroups)
        .then(resolve);
    });
  };

  const updateGroupLeader = (groupId: number, username: string) => {
    return new Promise((resolve) => {
      GroupService.updateGroupLeader(groupId, username).then(updateGroupsAndUsers).then(resolve);
    });
  };

  const addUserToGroup = (userId: number, groupId: number) => {
    return new Promise((resolve) => {
      GroupService.addUserToGroup(userId, groupId)
        .then(async () => {
          const userToMove = usersById[userId];

          if (userToMove.leader)
            await UserService.updateUser(userToMove.alias ?? userToMove.username, false, userToMove.isGPSEnabled);
        })
        .then(updateGroupsAndUsers)
        .then(resolve);
    });
  };

  const removeUserFromGroup = (userId: number, groupId: number) => {
    return new Promise((resolve) => {
      GroupService.removeUserFromGroup(userId, groupId)
        .then(async () => {
          const userToRemove = usersById[userId];
          if (userToRemove.leader)
            await UserService.updateUser(userToRemove.alias ?? userToRemove.username, false, userToRemove.isGPSEnabled);
        })
        .then(updateGroupsAndUsers)
        .then(resolve);
    });
  };

  return {
    removeUserFromGroup,
    addUserToGroup,
    getGroups,
    groups,
    groupsById,
    deleteGroup,
    createGroup,
    updateGroupName,
    updateGroupLeader,
  };
};

export default useGroups;
